/**
 * We import datadogRum as datadogDoNotUse just so that we can use it for typing. It shouldn't be used directly;
 * each app has its own copy of the datadog SDK and passes it to the `useUserTracking`
 * hook. This allows us to initialize datadog separately for each app so that we can
 * take advantage of setting the `service` and `version` values.
 */
import { datadogRum as datadogDoNotUse, RumEvent } from '@datadog/browser-rum';
import { useEffect } from 'react';

import { User } from '../classes/User';
import { getAnalytics } from '../utils/getAnaltyics';
import { getP44Env } from '../utils/getP44Env';
import { useFetch } from './useFetch';
import { useUser } from './useUser';

type DatadogSDK = typeof datadogDoNotUse;

/**
 * Matches the type of a single-spa import map.
 *
 * https://na14.voc.qa-integration.p-44.com/importmap.json
 */
interface ImportMap {
  imports: Record<string, string>;
}

interface Window {
  // This property is attached to the window object by the import-map-overrides package
  // that developers use during local development
  importMapOverrides?: {
    getOverrideMap: () => ImportMap;
  };
}

/**
 * Configuration tokens for Datadog RUM.
 *
 * https://p44.datadoghq.com/rum/application/2c373b06-4030-45dc-a85c-1a1a8c8ae47c/edit
 */
const DATADOG_APPLICATION_ID = '2c373b06-4030-45dc-a85c-1a1a8c8ae47c';
const DATADOG_CLIENT_TOKEN = 'pubc7ad67369305c92853d8e3be42b7f1fb';
const DATADOG_SITE = 'us5.datadoghq.com';

/**
 * The key used in the global Datadog context to determine if an app's Datadog SDK is enabled or disabled.
 */
const DATADOG_DISABLED_CONTEXT_KEY = 'disabled';

/**
 * Log debug messages only in lower environments.
 */
const logDebug = (message: string): void => {
  const env = getP44Env().ENV;
  if (env === 'qa-integration' || env === 'qa-stage') {
    console.log(message);
  }
};

/**
 * Parses the current app version from the import map.
 *
 * https://na14.voc.qa-integration.p-44.com/importmap.json
 */
const parseAppVersionFromImportMap = (args: {
  name: string;
  importMap: ImportMap;
}): string | undefined => {
  try {
    const appPath = args.importMap.imports[args.name];
    // The value in the import map is the full path to the js file for the given app, which includes the app's version, e.g.:
    // https://apps.resources.project44.com/admin-ui/1.0.18/project44-admin-ui.js
    // 0    0112                           23       34    4
    return appPath.split('/')[4];
  } catch (err) {
    logDebug(
      `Couldn't parse ${args.name}'s version from the import map: ${err}`,
    );
  }

  return undefined;
};

/**
 * Gets the local import map overrides, which is attached to the window object by the import-map-overrides package.
 *
 * Developers use this during local development to test their changes. Check if this app has a local override; if
 * it does, we will skip Datadog initialization so that we aren't sending error messages from local development to
 * Datadog.
 */
const checkAppHasLocalOverride = (args: { appName: string }): boolean => {
  const localOverrides =
    (window as unknown as Window).importMapOverrides?.getOverrideMap()
      .imports || {};
  if (args.appName in localOverrides) {
    logDebug(
      `Found local override for ${args.appName}: ${
        localOverrides[args.appName]
      }. Datadog will not be initialized.`,
    );
    return true;
  }
  return false;
};

const initDatadog = (args: {
  datadog: DatadogSDK;
  service: string;
  version: string;
}): void => {
  logDebug(`Initializing datadog with ${JSON.stringify(args)}`);

  const { ENV, GATEWAY_API_BASE_URL } = getP44Env();

  try {
    args.datadog.init({
      applicationId: DATADOG_APPLICATION_ID,
      clientToken: DATADOG_CLIENT_TOKEN,
      site: DATADOG_SITE,
      service: args.service,
      version: args.version,
      env: ENV,
      // Sampling configuration
      // https://github.com/DataDog/browser-sdk/blob/main/packages/rum/README.md#browser-and-browser-premium-sampling-configuration
      sampleRate: 100,
      premiumSampleRate: 100,
      // Tracks "frustration signals", automatically enables trackUserInteractions
      // https://docs.datadoghq.com/real_user_monitoring/frustration_signals/
      trackFrustrations: true,
      //Allows collecting feature flag data, currently in beta
      //https://docs.datadoghq.com/real_user_monitoring/feature_flag_tracking
      enableExperimentalFeatures: ['feature_flags'],
      // Allows "full stack" tracing of API calls.
      // https://docs.datadoghq.com/real_user_monitoring/connect_rum_and_traces/?tab=browserrum
      allowedTracingOrigins: [GATEWAY_API_BASE_URL],
      // init might be called multiple times if the user navigates away and then navigates
      // back to an app
      silentMultipleInit: true,
      // Filter out events from unmounted apps
      beforeSend: (event: RumEvent) => {
        // View events can't be dismissed using beforeSend:
        // https://github.com/DataDog/browser-sdk/blob/8daa52af9e10ab49af73b7ee85a9e61e789f75a8/packages/rum-core/src/domain/assembly.ts#L175
        // This means views will be reported for _each app_ the user interacted with during their session.
        if (event.type === 'view') {
          return true;
        }

        // This SDK is disabled. Don't send any events.
        if (event.context?.[DATADOG_DISABLED_CONTEXT_KEY]) {
          return false;
        }
      },
    });
  } catch (err) {
    logDebug(`Failed to initialize datadog with err: ${err}`);
  }
};

/**
 * Identifies the user for datadog.
 *
 * https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=npm#identify-user-sessions
 */
const setDatadogUser = (args: { datadog: DatadogSDK; user: User }): void => {
  logDebug(`Setting datadog user to ${args.user.username}`);

  try {
    args.datadog.setUser({
      id: args.user.id.toString(),
      email: args.user.username,
      name: args.user.getFullName(),
      tenant: args.user.companyName,
    });
  } catch (err) {
    logDebug(`Failed to set datadog user with err: ${err}`);
  }
};

/**
 * Enable the SDK by removing the `disabled` context key (if it's set)
 */
const enableDatadogSDK = (datadog: DatadogSDK): void => {
  datadog.removeRumGlobalContext(DATADOG_DISABLED_CONTEXT_KEY);
};

const enableDatadogSessionReplay = (datadog: DatadogSDK): void => {
  datadog.startSessionReplayRecording();
};

/**
 * Disable the SDK by adding `disabled: true` to the context
 */
const disableDatadogSDK = (datadog: DatadogSDK): void => {
  datadog.addRumGlobalContext(DATADOG_DISABLED_CONTEXT_KEY, true);
};

/**
 * Identifies the user for segment.
 *
 * https://segment.com/docs/connections/spec/identify/
 */
const setSegmentUser = (user: User): void => {
  logDebug(`Setting segment user to ${user.username}`);

  const analytics = getAnalytics();
  try {
    analytics?.identify(user.username, {
      userId: user.id,
      email: user.username,
      displayName: user.getFullName(),
      tenantId: user.tenantId,
      tenantUuid: user.tenantUuid,
      tenantName: user.companyName,
    });
  } catch (err) {
    logDebug(`Failed to set segment user with err: ${err}`);
  }
};

/**
 * Sets up user tracking for both Datadog and Segment.
 */
export function useUserTracking(datadog: DatadogSDK, appName: string) {
  const { data: user } = useUser({ skipRedirectOn401: true });
  const { data: importMap } = useFetch<ImportMap>({ url: '/importmap.json' });
  const appVersion = importMap
    ? parseAppVersionFromImportMap({ name: appName, importMap: importMap })
    : undefined;
  const appHasLocalOverride = checkAppHasLocalOverride({ appName });

  /**
   * Enable / disable the SDK on mount and unmount.
   */
  useEffect(() => {
    enableDatadogSDK(datadog);
    return () => disableDatadogSDK(datadog);
  }, []);

  /**
   * Initializes datadog for this application.
   */
  useEffect(() => {
    if (appVersion && !appHasLocalOverride) {
      initDatadog({ datadog, service: appName, version: appVersion });
      enableDatadogSessionReplay(datadog);
    }
  }, [appName, appVersion, appHasLocalOverride]);

  /**
   * Identifies the user for segment and datadog once the user is loaded.
   */
  useEffect(() => {
    if (user) {
      setSegmentUser(user);
      setDatadogUser({ datadog, user });
    }
  }, [user?.id]);
}
